import { ReactNode, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import {
  Building2,
  LayoutDashboard,
  Package,
  Users,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Search,
  LayoutGrid,
  Wallet,
  Receipt,
  Scale,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Shield,
  Briefcase,
  ClipboardList,
} from 'lucide-react';
import { CartDropdown } from '@/components/cart/CartDropdown';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { cn } from '@/lib/utils';
import { isTauri } from '@/lib/tauri';
import { StatusIndicators } from '@/components/desktop/StatusIndicators';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface DashboardLayoutProps {
  children: ReactNode;
}

const navigation = [
  { name: 'لوحة القيادة / Tableau de bord', shortName: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'التطبيقات / Applications', shortName: 'Apps', href: '/apps', icon: LayoutGrid },
  { name: 'نقطة البيع / POS', shortName: 'POS', href: '/apps/pos', icon: ShoppingCart, badge: 'NEW' },
  { name: 'الامتثال / Conformité', shortName: 'Conformité', href: '/apps/compliance', icon: Scale, badge: 'NEW' },
  { name: 'المنتجات / Produits', shortName: 'Produits', href: '/apps/products', icon: Package },
  { name: 'الخدمات / Services', shortName: 'Services', href: '/apps/services', icon: Briefcase },
  { name: 'العملاء / Clients', shortName: 'Clients', href: '/apps/clients', icon: Users },
  { name: 'الفواتير / Factures', shortName: 'Factures', href: '/apps/sales', icon: FileText },
  { name: 'عروض الأسعار / Devis', shortName: 'Devis', href: '/apps/quotes', icon: ClipboardList },
  { name: 'الخزينة / Trésorerie', shortName: 'Trésorerie', href: '/apps/treasury', icon: Wallet },
  { name: 'الشيكات / Chèques', shortName: 'Chèques', href: '/apps/treasury/checks', icon: Receipt },
  { name: 'التقارير / Rapports', shortName: 'Rapports', href: '/apps/reports', icon: BarChart3 },
  { name: 'الإعدادات / Paramètres', shortName: 'Paramètres', href: '/settings', icon: Settings },
];

const SIDEBAR_COLLAPSED_KEY = 'lebusiness-sidebar-collapsed';

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return saved === 'true';
    }
    return false;
  });
  const { profile, company, signOut } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = isTauri();

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: '/login' });
  };

  // Check if a navigation item is active
  const isActive = (href: string) => {
    // Exact match for dashboard and apps
    if (href === '/dashboard' || href === '/apps') {
      return location.pathname === href;
    }
    // For nested routes, check if pathname starts with the href
    return location.pathname.startsWith(href);
  };

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-screen bg-background">
        {/* Mobile sidebar */}
        <div
          className={`fixed inset-0 z-50 lg:hidden ${
            sidebarOpen ? 'block' : 'hidden'
          }`}
        >
          <div className="fixed inset-0 bg-gray-900/80 dark:bg-black/80" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border">
            <div className="flex h-16 items-center justify-between px-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-lg text-foreground">leBusiness</span>
              </div>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="w-6 h-6 text-muted-foreground" />
              </button>
            </div>
            <nav className="px-3 py-4 space-y-1">
              {navigation.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                      active
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    )}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1">{item.name}</span>
                    {item.badge && (
                      <span className="px-2 py-0.5 text-xs font-bold bg-emerald-500 text-white rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
              {/* Subscription link in mobile */}
              <Link
                to="/settings/subscription"
                className={cn(
                  "flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                  isActive('/settings/subscription')
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )}
                onClick={() => setSidebarOpen(false)}
              >
                <CreditCard className="w-5 h-5" />
                <span className="flex-1">الاشتراك / Abonnement</span>
              </Link>
            </nav>
          </div>
        </div>

        {/* Desktop sidebar */}
        <div
          className={cn(
            "hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col border-r border-border bg-card transition-all duration-300",
            isCollapsed ? "lg:w-16" : "lg:w-64"
          )}
        >
          {/* Header */}
          <div className={cn(
            "flex h-16 items-center border-b border-border transition-all duration-300",
            isCollapsed ? "px-2 justify-center" : "px-6 gap-3"
          )}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg flex-shrink-0">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            {!isCollapsed && (
              <div className="min-w-0 flex-1">
                <span className="font-bold text-xl bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  leBusiness
                </span>
                <p className="text-xs text-muted-foreground truncate">{company?.name || 'My Company'}</p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const active = isActive(item.href);

              if (isCollapsed) {
                return (
                  <Tooltip key={item.href}>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.href}
                        className={cn(
                          "flex items-center justify-center w-12 h-12 mx-auto rounded-lg transition-colors relative",
                          active
                            ? "bg-accent text-accent-foreground shadow-sm"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        )}
                      >
                        <item.icon className="w-5 h-5" />
                        {item.badge && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full" />
                        )}
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{item.shortName}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors",
                    active
                      ? "bg-accent text-accent-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="flex-1">{item.name}</span>
                  {item.badge && (
                    <span className="px-2 py-0.5 text-xs font-bold bg-emerald-500 text-white rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}

            {/* Subscription Link - Desktop Sidebar */}
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/settings/subscription"
                    className={cn(
                      "flex items-center justify-center w-12 h-12 mx-auto rounded-lg transition-colors relative",
                      isActive('/settings/subscription')
                        ? "bg-accent text-accent-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    )}
                  >
                    <CreditCard className="w-5 h-5" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Abonnement</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Link
                to="/settings/subscription"
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors",
                  isActive('/settings/subscription')
                    ? "bg-accent text-accent-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <CreditCard className="w-5 h-5" />
                <span className="flex-1">الاشتراك / Abonnement</span>
              </Link>
            )}
          </nav>

          {/* Collapse toggle */}
          <div className="border-t border-border p-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleCollapse}
                  className={cn(
                    "flex items-center justify-center w-full h-10 rounded-lg text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors",
                    isCollapsed ? "px-0" : "px-3"
                  )}
                >
                  {isCollapsed ? (
                    <ChevronRight className="w-5 h-5" />
                  ) : (
                    <>
                      <ChevronLeft className="w-5 h-5 mr-2" />
                      <span className="text-sm">Réduire</span>
                    </>
                  )}
                </button>
              </TooltipTrigger>
              {isCollapsed && (
                <TooltipContent side="right">
                  <p>Expand sidebar</p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>

          {/* Super Admin Link */}
          {profile?.isSuperAdmin && (
            <div className={cn('border-t border-border p-2', isCollapsed && 'p-2')}>
              {isCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/admin/dashboard"
                      className="flex items-center justify-center w-12 h-12 mx-auto rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-500/20 text-violet-400 hover:from-violet-500/30 hover:to-purple-500/30 transition-colors"
                    >
                      <Shield className="w-5 h-5" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>Admin Panel</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Link
                  to="/admin/dashboard"
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg bg-gradient-to-r from-violet-500/20 to-purple-500/20 text-violet-400 hover:from-violet-500/30 hover:to-purple-500/30 transition-colors"
                >
                  <Shield className="w-5 h-5" />
                  <span className="flex-1">Admin Panel</span>
                </Link>
              )}
            </div>
          )}

          {/* User section */}
          <div className={cn(
            "border-t border-border p-3 transition-all duration-300",
            isCollapsed && "p-2"
          )}>
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center justify-center w-12 h-12 mx-auto rounded-lg text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{profile?.fullName || 'User'}</p>
                  <p className="text-xs text-muted-foreground">Déconnexion</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-700 dark:text-emerald-400 font-semibold">
                      {profile?.fullName?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {profile?.fullName || 'User'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{profile?.email}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={handleSignOut}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  تسجيل الخروج / Déconnexion
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className={cn(
          "transition-all duration-300",
          isCollapsed ? "lg:pl-16" : "lg:pl-64"
        )}>
          {/* Top header */}
          <div className="sticky top-0 z-40 flex h-16 bg-card border-b border-border">
            <button
              type="button"
              className="px-4 text-muted-foreground lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex flex-1 items-center justify-between px-4 sm:px-6">
              {/* Status indicators for desktop app - Always visible in header */}
              {isDesktop && (
                <div className="hidden sm:flex items-center">
                  <StatusIndicators />
                </div>
              )}

              {/* Search */}
              <div className={cn("flex-1 max-w-2xl", isDesktop && "ml-4")}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="search"
                    placeholder="بحث... / Rechercher..."
                    className="w-full pl-10 pr-4 py-2 bg-background border border-border text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-4 ml-4">
                {/* Draft Invoice Cart Dropdown */}
                <CartDropdown />

                {/* Notification Bell */}
                <NotificationBell />

                {/* Theme Toggle */}
                <ThemeToggle />

                {/* Mobile user menu */}
                <div className="lg:hidden">
                  <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-emerald-700 font-semibold text-sm">
                        {profile?.fullName?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Page content */}
          <main className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
